import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection, doc,
  getDocs, addDoc, setDoc, updateDoc, deleteDoc,
  query, orderBy,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Feature, FeatureCategory, GroupedFeatures } from '../models/feature.model';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { fromCollectionSnapshot } from '../shared/firestore/live-query';

// ── Seed data ────────────────────────────────────────────────────────────────

const SEED_CATEGORIES: FeatureCategory[] = [
  { id: 'espacio',       label: 'Espacio',        icon: 'maximize-2',    order: 1 },
  { id: 'tecnologia',    label: 'Tecnología',      icon: 'monitor',       order: 2 },
  { id: 'comodidades',   label: 'Comodidades',     icon: 'star',          order: 3 },
  { id: 'accesibilidad', label: 'Accesibilidad',   icon: 'accessibility', order: 4 },
];

const SEED_FEATURES: Feature[] = [
  // ── Espacio ──────────────────────────────────────────────────────────────────────────────────────────────
  { id: 'sala-grande',   label: 'Sala amplia (+50 pax)',      icon: 'maximize-2',    categoryId: 'espacio',       allowedCategories: ['global'],                                          isActive: true,  sortOrder: 1 },
  { id: 'ciclorama',     label: 'Ciclorama',                  icon: 'layout',        categoryId: 'espacio',       allowedCategories: ['estudio'],                                         isActive: true,  sortOrder: 2 },
  { id: 'exterior',      label: 'Terraza o patio exterior',   icon: 'sunrise',       categoryId: 'espacio',       allowedCategories: ['terraza', 'singular'],                             isActive: true,  sortOrder: 3 },
  { id: 'cocina-equip',  label: 'Cocina equipada',            icon: 'chef-hat',      categoryId: 'espacio',       allowedCategories: ['cocina', 'sala_fiestas', 'singular'],              isActive: true,  sortOrder: 4 },
  { id: 'horno-prof',    label: 'Horno profesional',          icon: 'flame',         categoryId: 'espacio',       allowedCategories: ['cocina'],                                          isActive: true,  sortOrder: 5 },
  { id: 'isla-central',  label: 'Isla central de trabajo',    icon: 'grid-3x3',      categoryId: 'espacio',       allowedCategories: ['cocina'],                                          isActive: true,  sortOrder: 6 },
  { id: 'guirnaldas',    label: 'Guirnaldas de luces',        icon: 'sparkles',      categoryId: 'espacio',       allowedCategories: ['terraza', 'sala_fiestas'],                         isActive: true,  sortOrder: 7 },
  { id: 'piscina-bolas', label: 'Piscina de bolas',           icon: 'circle-dot',    categoryId: 'espacio',       allowedCategories: ['sala_fiestas'],                                    isActive: true,  sortOrder: 8 },
  // ── Tecnología ───────────────────────────────────────────────────────────────────────────────────────────
  { id: 'wifi',          label: 'WiFi alta velocidad',        icon: 'wifi',          categoryId: 'tecnologia',    allowedCategories: ['global'],                                          isActive: true,  sortOrder: 1 },
  { id: 'pantalla-tv',   label: 'Pantalla TV / Monitor',      icon: 'tv',            categoryId: 'tecnologia',    allowedCategories: ['global'],                                          isActive: true,  sortOrder: 2 },
  { id: 'proyector',     label: 'Proyector / Cañón',          icon: 'projector',     categoryId: 'tecnologia',    allowedCategories: ['coworking', 'singular'],                           isActive: true,  sortOrder: 3 },
  { id: 'sonido',        label: 'Sistema de sonido',          icon: 'volume-2',      categoryId: 'tecnologia',    allowedCategories: ['sala_fiestas', 'estudio', 'singular'],             isActive: true,  sortOrder: 4 },
  { id: 'iluminacion',   label: 'Iluminación profesional',    icon: 'sun',           categoryId: 'tecnologia',    allowedCategories: ['estudio', 'singular'],                             isActive: true,  sortOrder: 5 },
  { id: 'pizarra',       label: 'Pizarra / Whiteboard',       icon: 'pencil-line',   categoryId: 'tecnologia',    allowedCategories: ['coworking', 'singular'],                           isActive: true,  sortOrder: 6 },
  { id: 'video-conf',    label: 'Videoconferencia integrada', icon: 'video',         categoryId: 'tecnologia',    allowedCategories: ['coworking', 'estudio', 'singular'],                isActive: true,  sortOrder: 7 },
  // ── Comodidades ──────────────────────────────────────────────────────────────────────────────────────────
  { id: 'climatizado',   label: 'Climatización A/C',          icon: 'thermometer',   categoryId: 'comodidades',   allowedCategories: ['global'],                                          isActive: true,  sortOrder: 1 },
  { id: 'aparcamiento',  label: 'Aparcamiento',               icon: 'car-front',     categoryId: 'comodidades',   allowedCategories: ['global'],                                          isActive: true,  sortOrder: 2 },
  { id: 'banos',         label: 'Baños adicionales',          icon: 'droplets',      categoryId: 'comodidades',   allowedCategories: ['global'],                                          isActive: true,  sortOrder: 3 },
  { id: 'vestuario',     label: 'Vestuario / Taquillas',      icon: 'shirt',         categoryId: 'comodidades',   allowedCategories: ['estudio', 'singular'],                             isActive: true,  sortOrder: 4 },
  { id: 'recepcion',     label: 'Recepción / Conserjería',    icon: 'bell',          categoryId: 'comodidades',   allowedCategories: ['coworking', 'singular'],                           isActive: true,  sortOrder: 5 },
  // ── Accesibilidad ────────────────────────────────────────────────────────────────────────────────────────
  { id: 'accesible',     label: 'Accesible PMR',              icon: 'accessibility', categoryId: 'accesibilidad', allowedCategories: ['global'],                                          isActive: true,  sortOrder: 1 },
  { id: 'ascensor',      label: 'Ascensor',                   icon: 'arrow-up-down', categoryId: 'accesibilidad', allowedCategories: ['global'],                                          isActive: true,  sortOrder: 2 },
];

// ── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class FeaturesAgentService extends FirebaseAwareService {
  private firestore = inject(Firestore);

  // Cached arrays for wizard (imperative path — populated by load())
  private _categories: FeatureCategory[] = [];
  private _features:   Feature[]         = [];
  private _loaded = false;

  // ── Wizard: imperative load + filter ──────────────────────────────────────

  /** Fetches categories + features from Firestore, seeding on first run. */
  async load(): Promise<void> {
    if (this._loaded) return;

    const [catSnap, featSnap] = await this.inCtx(() => Promise.all([
      getDocs(query(collection(this.firestore, 'categories'), orderBy('order'))),
      getDocs(collection(this.firestore, 'features')),
    ]));

    if (catSnap.empty) {
      try { await this._seed(); } catch { /* non-admin: keep in-memory only */ }
      this._categories = [...SEED_CATEGORIES];
      this._features   = [...SEED_FEATURES];
    } else {
      this._categories = catSnap.docs.map(d => ({ id: d.id, ...d.data() } as FeatureCategory));
      this._features   = featSnap.docs.map(d => ({ id: d.id, ...d.data() } as Feature));
    }

    this._loaded = true;
  }

  get categories(): FeatureCategory[] { return this._categories; }
  get features():   Feature[]         { return this._features;   }

  /**
   * Returns features grouped by category, filtered to the space's own categories.
   *
   * Inclusion rule (OR logic for multipurpose spaces):
   *  - `isActive` must be true
   *  - `allowedCategories` is empty, contains 'global', OR contains at least
   *    one value from `spaceCategories` (union → no duplicates).
   */
  getFilteredGrouped(spaceCategories: string[]): GroupedFeatures[] {
    const active = this._features.filter(f => {
      if (!f.isActive) return false;
      const allowed = f.allowedCategories ?? [];
      if (!allowed.length || allowed.includes('global')) return true;
      return spaceCategories.some(cat => allowed.includes(cat));
    });

    return this._categories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(cat => ({
        category: cat,
        features: active
          .filter(f => f.categoryId === cat.id)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      }))
      .filter(g => g.features.length > 0);
  }

  /**
   * Active features with empty/`global` allowedCategories only.
   * Do not use for public space detail — use {@link groupedByFeatureIds}.
   */
  grouped(): GroupedFeatures[] {
    return this.getFilteredGrouped([]);
  }

  /**
   * Groups the owner's selected feature ids for public display.
   * Ignores `allowedCategories` (selection in the wizard is the source of truth).
   */
  groupedByFeatureIds(selectedIds: Iterable<string>): GroupedFeatures[] {
    const ids = new Set(selectedIds);
    if (ids.size === 0) return [];

    const selected = this._features.filter(f => f.isActive && ids.has(f.id));
    const knownCategoryIds = new Set(this._categories.map(c => c.id));

    const groups = this._categories
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(cat => ({
        category: cat,
        features: selected
          .filter(f => f.categoryId === cat.id)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      }))
      .filter(g => g.features.length > 0);

    const orphans = selected
      .filter(f => !knownCategoryIds.has(f.categoryId))
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    if (orphans.length > 0) {
      groups.push({
        category: {
          id: '_uncategorized',
          label: 'Equipamiento',
          icon: 'package',
          order: 9999,
        },
        features: orphans,
      });
    }

    return groups;
  }

  // ── Admin: reactive live streams ──────────────────────────────────────────

  watchAllFeatures(): Observable<Feature[]> {
    // No ordering in the query — the grouped() computed in the component
    // sorts by category.order and feature.sortOrder client-side, so no
    // composite index is required here.
    const q = this.inCtx(() => query(collection(this.firestore, 'features')));
    return fromCollectionSnapshot<Feature>(this.injector, q);
  }

  watchAllCategories(): Observable<FeatureCategory[]> {
    const q = this.inCtx(() =>
      query(collection(this.firestore, 'categories'), orderBy('order')),
    );
    return fromCollectionSnapshot<FeatureCategory>(this.injector, q);
  }

  // ── Admin: CRUD ───────────────────────────────────────────────────────────

  async createFeature(data: Omit<Feature, 'id'>): Promise<string> {
    const ref = await this.inCtx(() =>
      addDoc(collection(this.firestore, 'features'), data),
    );
    return ref.id;
  }

  async updateFeature(id: string, data: Partial<Omit<Feature, 'id'>>): Promise<void> {
    await this.inCtx(() =>
      updateDoc(doc(this.firestore, 'features', id), { ...data }),
    );
  }

  async deleteFeature(id: string): Promise<void> {
    await this.inCtx(() => deleteDoc(doc(this.firestore, 'features', id)));
  }

  async createCategory(data: Omit<FeatureCategory, 'id'>): Promise<string> {
    const ref = await this.inCtx(() =>
      addDoc(collection(this.firestore, 'categories'), data),
    );
    return ref.id;
  }

  async updateCategory(id: string, data: Partial<Omit<FeatureCategory, 'id'>>): Promise<void> {
    await this.inCtx(() =>
      updateDoc(doc(this.firestore, 'categories', id), { ...data }),
    );
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private async _seed(): Promise<void> {
    await this.inCtx(() => Promise.all([
      ...SEED_CATEGORIES.map(c =>
        setDoc(doc(this.firestore, 'categories', c.id), {
          label: c.label, icon: c.icon, order: c.order,
        }),
      ),
      ...SEED_FEATURES.map(f =>
        setDoc(doc(this.firestore, 'features', f.id), {
          label:             f.label,
          icon:              f.icon,
          categoryId:        f.categoryId,
          allowedCategories: f.allowedCategories,
          isActive:          f.isActive,
          sortOrder:         f.sortOrder ?? 0,
        }),
      ),
    ]));
  }
}
